import React, { useState, useEffect } from 'react';
import { RFQ, RFQCreate } from './types';
import { authApi, rfqApi } from './services/api';
import RFQList from './components/RFQList';
import AddRFQForm from './components/AddRFQForm';
import LoginForm from './components/LoginForm';
import RFQDetails from './components/RFQDetails';
import RFQReport from './components/RFQReport';
import EquipmentBrowser from './components/EquipmentBrowser';
import LaborRatesPage from './components/LaborRatesPage';
import ProcessTemplatesPage from './components/ProcessTemplatesPage';
import './App.css';

type NavKey = 'rfqs' | 'equipment' | 'process-templates' | 'labor';

const App: React.FC = () => {
    const [rfqs, setRfqs] = useState<RFQ[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showAddForm, setShowAddForm] = useState<boolean>(false);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
        Boolean(localStorage.getItem('rfq_access_token'))
    );
    const [selectedRfqId, setSelectedRfqId] = useState<string | null>(null);
    const [printRfqId, setPrintRfqId] = useState<string | null>(null);
    const [printDetail, setPrintDetail] = useState<'itemized' | 'aggregated'>(
        'itemized'
    );
    const [showEquipmentPage, setShowEquipmentPage] = useState<boolean>(false);
    const [showLaborRatesPage, setShowLaborRatesPage] = useState<boolean>(false);
    const [showProcessTemplatesPage, setShowProcessTemplatesPage] = useState<boolean>(false);

    useEffect(() => {
        const syncFromPath = () => {
            const path = window.location.pathname;
            const printMatch = path.match(/^\/rfqs\/([^/]+)\/print$/);
            if (printMatch && printMatch[1]) {
                setPrintRfqId(printMatch[1]);
                const detailParam = new URLSearchParams(window.location.search).get(
                    'detail'
                );
                setPrintDetail(detailParam === 'aggregated' ? 'aggregated' : 'itemized');
                setSelectedRfqId(null);
                setShowEquipmentPage(false);
                setShowLaborRatesPage(false);
                setShowProcessTemplatesPage(false);
                return;
            }
            const rfqMatch = path.match(/^\/rfqs\/([^/]+)$/);
            if (rfqMatch && rfqMatch[1]) {
                setPrintRfqId(null);
                setSelectedRfqId(rfqMatch[1]);
                setShowEquipmentPage(false);
                setShowLaborRatesPage(false);
                setShowProcessTemplatesPage(false);
                return;
            }
            if (path === '/equipment') {
                setPrintRfqId(null);
                setSelectedRfqId(null);
                setShowEquipmentPage(true);
                setShowLaborRatesPage(false);
                setShowProcessTemplatesPage(false);
                return;
            }
            if (path === '/labor-rates') {
                setPrintRfqId(null);
                setSelectedRfqId(null);
                setShowEquipmentPage(false);
                setShowLaborRatesPage(true);
                setShowProcessTemplatesPage(false);
                return;
            }
            if (path === '/process-templates') {
                setPrintRfqId(null);
                setSelectedRfqId(null);
                setShowEquipmentPage(false);
                setShowLaborRatesPage(false);
                setShowProcessTemplatesPage(true);
                return;
            }
            setPrintRfqId(null);
            setSelectedRfqId(null);
            setShowEquipmentPage(false);
            setShowLaborRatesPage(false);
            setShowProcessTemplatesPage(false);
        };

        syncFromPath();
        window.addEventListener('popstate', syncFromPath);
        return () => {
            window.removeEventListener('popstate', syncFromPath);
        };
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            loadRFQs();
        } else {
            setIsLoading(false);
        }
    }, [isAuthenticated]);

    const loadRFQs = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await rfqApi.list();
            setRfqs(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load RFQs');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateRFQ = async (rfq: RFQCreate) => {
        await rfqApi.create(rfq);
        setShowAddForm(false);
        await loadRFQs();
    };

    const handleLogout = () => {
        authApi.logout();
        setIsAuthenticated(false);
        setRfqs([]);
        setSelectedRfqId(null);
        setShowEquipmentPage(false);
        setShowLaborRatesPage(false);
        setShowProcessTemplatesPage(false);
        window.history.replaceState(null, '', '/');
    };

    const handleSelectRfq = (rfqId: string) => {
        setSelectedRfqId(rfqId);
        setShowEquipmentPage(false);
        setShowLaborRatesPage(false);
        setShowProcessTemplatesPage(false);
        window.history.pushState(null, '', `/rfqs/${rfqId}`);
    };

    const handleBackToList = () => {
        setSelectedRfqId(null);
        setShowEquipmentPage(false);
        setShowLaborRatesPage(false);
        setShowProcessTemplatesPage(false);
        window.history.pushState(null, '', '/');
    };

    const handleShowEquipment = () => {
        setSelectedRfqId(null);
        setShowEquipmentPage(true);
        setShowLaborRatesPage(false);
        setShowProcessTemplatesPage(false);
        window.history.pushState(null, '', '/equipment');
    };

    const handleShowLaborRates = () => {
        setSelectedRfqId(null);
        setShowEquipmentPage(false);
        setShowLaborRatesPage(true);
        setShowProcessTemplatesPage(false);
        window.history.pushState(null, '', '/labor-rates');
    };

    const handleShowProcessTemplates = () => {
        setSelectedRfqId(null);
        setShowEquipmentPage(false);
        setShowLaborRatesPage(false);
        setShowProcessTemplatesPage(true);
        window.history.pushState(null, '', '/process-templates');
    };

    const handleShowRfqs = () => {
        setSelectedRfqId(null);
        setShowEquipmentPage(false);
        setShowLaborRatesPage(false);
        setShowProcessTemplatesPage(false);
        window.history.pushState(null, '', '/');
    };

    const activeKey: NavKey = showEquipmentPage
        ? 'equipment'
        : showProcessTemplatesPage
          ? 'process-templates'
          : showLaborRatesPage
            ? 'labor'
            : 'rfqs';

    if (printRfqId) {
        return <RFQReport rfqId={printRfqId} detail={printDetail} />;
    }

    if (!isAuthenticated) {
        return <LoginForm onLoginSuccess={() => setIsAuthenticated(true)} />;
    }

    if (isLoading) {
        return (
            <div className="min-h-screen grid-bg ff-display text-[#141414] flex items-center justify-center">
                <div className="ff-mono text-sm text-[#141414]/60">
                    Loading RFQs…
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen grid-bg ff-display text-[#141414]">
            <header className="border-b border-[#141414]/15 bg-[#f3efe6]/95 backdrop-blur sticky top-0 z-20">
                <div className="max-w-[1280px] mx-auto px-6 py-4 flex items-center justify-between gap-6 flex-wrap">
                    <div>
                        <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#e0461e] flex items-center gap-2">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#e0461e] animate-pulse" />
                            RFQ Processing System
                        </div>
                        <h1 className="ff-serif text-4xl leading-none mt-1">
                            Conveyor line quoting
                        </h1>
                    </div>

                    <nav className="flex items-stretch border border-[#141414]/25 bg-white/60">
                        <NavButton
                            active={activeKey === 'rfqs' && !selectedRfqId}
                            onClick={handleShowRfqs}
                            label="RFQs"
                        />
                        <NavButton
                            active={activeKey === 'equipment'}
                            onClick={handleShowEquipment}
                            label="Equipment"
                        />
                        <NavButton
                            active={activeKey === 'process-templates'}
                            onClick={handleShowProcessTemplates}
                            label="Process templates"
                        />
                        <NavButton
                            active={activeKey === 'labor'}
                            onClick={handleShowLaborRates}
                            label="Labor rates"
                        />
                        <button
                            type="button"
                            onClick={handleLogout}
                            className="ff-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border-l border-[#141414]/15 text-[#141414]/70 hover:bg-[#141414] hover:text-[#f3efe6] transition"
                        >
                            Log out
                        </button>
                    </nav>
                </div>
            </header>

            <div className="max-w-[1280px] mx-auto px-6 py-8">
                {error && (
                    <div className="mb-4 border border-[#c2251a] bg-[#c2251a]/10 text-[#c2251a] px-4 py-3 ff-mono text-sm">
                        {error}
                    </div>
                )}

                {showEquipmentPage ? (
                    <section className="bg-white border border-[#141414]/20">
                        <div className="px-6 py-4 border-b border-[#141414]/15">
                            <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#e0461e]">
                                Inventory
                            </div>
                            <h2 className="ff-serif text-3xl leading-none mt-1">
                                Equipment
                            </h2>
                            <p className="ff-mono text-[11px] text-[#141414]/55 mt-2">
                                Equipment available for configuring conveyor stations. Costs
                                below are per unit and include goods plus default labor.
                            </p>
                        </div>
                        <div className="p-6">
                            <EquipmentBrowser />
                        </div>
                    </section>
                ) : showProcessTemplatesPage ? (
                    <section className="bg-white border border-[#141414]/20">
                        <div className="px-6 py-4 border-b border-[#141414]/15">
                            <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#e0461e]">
                                Inventory
                            </div>
                            <h2 className="ff-serif text-3xl leading-none mt-1">
                                Process templates
                            </h2>
                            <p className="ff-mono text-[11px] text-[#141414]/55 mt-2">
                                Reusable bundles of equipment lines that can be applied to a
                                conveyor station in one step (e.g. a complete welding cell).
                            </p>
                        </div>
                        <div className="p-6">
                            <ProcessTemplatesPage />
                        </div>
                    </section>
                ) : showLaborRatesPage ? (
                    <section className="bg-white border border-[#141414]/20">
                        <div className="px-6 py-4 border-b border-[#141414]/15">
                            <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#e0461e]">
                                Inventory
                            </div>
                            <h2 className="ff-serif text-3xl leading-none mt-1">
                                Labor rates
                            </h2>
                            <p className="ff-mono text-[11px] text-[#141414]/55 mt-2">
                                Hourly rates applied to equipment labor hours. Changes affect
                                all cost breakdowns on the conveyor station editor.
                            </p>
                        </div>
                        <div className="p-6">
                            <LaborRatesPage />
                        </div>
                    </section>
                ) : selectedRfqId ? (
                    <RFQDetails
                        rfqId={selectedRfqId}
                        onBack={handleBackToList}
                        onUpdated={loadRFQs}
                    />
                ) : (
                    <>
                        {showAddForm ? (
                            <div className="mb-8">
                                <AddRFQForm
                                    onSubmit={handleCreateRFQ}
                                    onCancel={() => setShowAddForm(false)}
                                />
                            </div>
                        ) : (
                            <div className="mb-6">
                                <button
                                    type="button"
                                    className="ff-mono text-[10px] uppercase tracking-wider px-3 py-2 bg-[#141414] text-[#f3efe6] hover:bg-[#e0461e] transition"
                                    onClick={() => setShowAddForm(true)}
                                >
                                    + Add new RFQ
                                </button>
                            </div>
                        )}

                        <section className="bg-white border border-[#141414]/20">
                            <RFQList rfqs={rfqs} onSelectRfq={handleSelectRfq} />
                        </section>
                    </>
                )}
            </div>
        </div>
    );
};

const NavButton: React.FC<{
    active: boolean;
    onClick: () => void;
    label: string;
}> = ({ active, onClick, label }) => (
    <button
        type="button"
        onClick={onClick}
        className={`ff-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border-l first:border-l-0 border-[#141414]/15 transition ${
            active
                ? 'bg-[#141414] text-[#f3efe6]'
                : 'text-[#141414]/70 hover:bg-[#141414]/5'
        }`}
    >
        {label}
    </button>
);

export default App;
